<template>
	<div class="app-layout">
		
		<div class="grey-gradient" />

		<div 
			class="top-bar"
			:class="{
				'is-admin': $page.props.user.user_type == 'admin'
			}"
			>
			<div class="logo-container">
				<Link :href="route('cases')">
					<img src="/images/logo-orthomatrix.svg" />
				</Link>
			</div>

			<div class="user-account-box">
				<div class="user-type">
					{{ niceUserTypeDisplay($page.props.user.user_type) }}{{ $page.props.user.tag != null ? ` - ${$page.props.user.tag}` : '' }}
				</div>

				<div 
					class="notifications-area"
					:class="[
						$page.props.activity && $page.props.activity.length > 0 ? 'new-notifications' : ''
					]"
					v-click-outside="closeNotifications"
					>
					<i 
						class="fal fa-bell"
						@click="showNotifications = !showNotifications"
						></i>

					<div 
						class="notifications-box"
						:class="{
							'active': showNotifications	
						}"
						>
						<div class="has-notifications" v-if="$page.props.activity && $page.props.activity.length > 0">
							<div class="list-container">
								<div 
									class="row"
									v-for="(activity, index) in $page.props.activity"
									:key="index"
									@click="dismissAndGoto(activity.id)"
									>
									<div class="activity">
										<template v-if="activity.simple_message">
											<div class="message">
												{{ activity.simple_message }}
											</div>
										</template>
										<template v-else>
											<div class="message" v-if="['admin'].includes($page.props.user.user_type)">
												<strong v-if="activity.user != null && activity.user.name != null">{{ activity.user.name }}</strong> {{ activity.message }} on case <strong>{{ projectIdentifier(activity.project) }}</strong>
											</div>
											<div class="message" v-else>
												<strong>OrthoMatrix</strong> {{ activity.message }} on case <strong>{{ projectIdentifier(activity.project) }}</strong>
											</div>
										</template>
									</div>
									<div class="meta-bar">
										<div class="date">
											{{ timeSince(activity.created_at) }}
										</div>
									</div>
								</div>
							</div>

							<div class="dismiss-all" @click="dismissAll">Dismiss all</div>
						</div>

						<div class="notifications-empty" v-else>
							You have no new notifications.
						</div>

					</div>
				</div>

				<div class="dark-box">
					<div class="avatar">
						<Link 
							title="My Account"
							:href="route('profile.show')">
							<img :src="$page.props.user.profile_photo_url" />
						</Link>
					</div>
					<div class="user-details">
						<div class="user-name">
							<Link :href="route('profile.show')">
								{{ $page.props.user.name }}
							</Link>
						</div>
						<div class="sign-out">
							<Link @click="logout">
								Sign out <i class="fal fa-sign-out-alt"></i>
							</Link>
						</div>
					</div>

					<div 
						class="mobile-hamburger" 
						@click="toggleMobileNav" 
						:class="[
							mobileNavOpen ? 'active' : ''
						]"
						/>
				</div>
			</div>
		</div>

		<div 
			class="side-bar"
			:class="[
				collapseSidebar ? 'collapsed' : '',
				mobileNavOpen ? 'mobile-nav-open' : ''
			]"
			>
			<ul class="side-nav">
				<li class="collapse-sidebar-toggle">
					<a @click="toggleCollapseSidebar">
						<div class="menu-icon">
							<i
								v-if="!collapseSidebar" 
								class="fal fa-chevron-double-left"></i>
							<i 
								v-if="collapseSidebar" 
								class="fal fa-chevron-double-right"></i>
						</div>
					</a>
				</li>
				<li>
					<Link 
						title="Cases"
						:href="route('cases')"
						@click="closeMobileNav"
						>
						<div class="menu-icon">
							<i class="fal fa-file-user"></i>
						</div>
						<span class="label">Cases</span>
					</Link>
				</li>
				<li>
					<Link 
						title="Document Library"
						:href="route('doc-library')"
						@click="closeMobileNav"
						>
						<div class="menu-icon">
							<i class="fal fa-books"></i>
						</div>
						<span class="label">Document Library</span>
					</Link>
				</li>
				<li v-if="$page.props.user.user_type == 'admin'">
					<Link 
						title="Default Enquiry Forms"	
						:href="route('enquiry-forms')"
						@click="closeMobileNav"
						>
						<div class="menu-icon">
							<i class="fal fa-file-pdf"></i>
						</div>
						<span class="label">Default Enquiry Forms</span>
					</Link>
				</li>
				<li>
					<Link 
						title="My Account"
						:href="route('profile.show')"
						@click="closeMobileNav"
						>
						<div class="menu-icon">
							<i class="fal fa-user"></i>
						</div>
						<span class="label">My Account</span>
					</Link>
				</li>
				<li v-if="$page.props.user.user_type == 'admin'">
					<Link 
						title="User Management"
						:href="route('user-management')"
						@click="closeMobileNav"
						>
						<div class="menu-icon">
							<i class="fal fa-users"></i>
						</div>
						<span class="label">User Management</span>
					</Link>
				</li>
				<li v-if="['admin'].includes($page.props.user.user_type)">
					<Link 
						title="Add New Users"
						:href="route('bulk-add-users')"
						@click="closeMobileNav"
						>
						<div class="menu-icon">
							<i class="fal fa-users-medical"></i>
						</div>
						<span class="label">Add New Users</span>
					</Link>
				</li>
				<li v-if="$page.props.user.user_type == 'admin'">
					<Link 
						title="Forms"
						:href="route('forms')"
						@click="closeMobileNav"
						>
						<div class="menu-icon">
							<i class="fal fa-poll"></i>
						</div>
						<span class="label">PMS Forms</span>
					</Link>
				</li>
				<li v-if="$page.props.globalPermissions.canSetGlobalNotificationSettings">
					<Link 
						title="Notification Settings"
						:href="route('notification-log')"
						@click="closeMobileNav"
						>
						<div class="menu-icon">
							<i class="fal fa-bells"></i>
						</div>
						<span class="label">Notification Logs</span>
					</Link>
				</li>

				<li v-if="mobileNavOpen">
					<Link @click="logout">
						<div class="menu-icon">
							<i class="fal fa-sign-out-alt"></i>
						</div>
						<span class="label">Sign out</span>
					</Link>
				</li>

			</ul>
		</div>

		<!-- Page Content -->
		<main
			:class="[collapseSidebar ? 'collapsed-sidebar' : '']"
			>
			<div class="content-container">
				<transition name="fade" mode="out-in">
					<slot></slot>
				</transition>
			</div>
		</main>

		<footer>
			<div class="app-version">Version: {{ appVersion }}</div>
		</footer>


	</div>
</template>

<script>
	// import JetBanner from '@/Jetstream/Banner.vue'
	// import JetDropdown from '@/Jetstream/Dropdown.vue'
	// import JetDropdownLink from '@/Jetstream/DropdownLink.vue'
	// import JetNavLink from '@/Jetstream/NavLink.vue'
	// import JetResponsiveNavLink from '@/Jetstream/ResponsiveNavLink.vue'
	import { Link } from '@inertiajs/inertia-vue3';

	export default {
		props: {
			title: String,
		},

		components: {
			Link,
		},

		mounted(){
			// console.log('page', this.$page)
			// if screen width is below 1512 then collapse sidebar
			if(window.innerWidth < 1512){
				this.collapseSidebar = true
			}
		},

		data() {
			return {
				showNotifications: false,
				showFlash: false,
				appVersion: '1.3.0',
				collapseSidebar: false,
				mobileNavOpen: false,
			}
		},

		methods: {
			toggleMobileNav(){
				this.mobileNavOpen = !this.mobileNavOpen
			},
			closeMobileNav(){
				this.mobileNavOpen = false
			},
			toggleCollapseSidebar(){
				this.collapseSidebar = !this.collapseSidebar
			},
			closeNotifications(){
				this.showNotifications = false
			},
			logout() {
				this.$inertia.post(route('logout'));
			},
			projectIdentifier(project){
				if(project != null){
					return project.enquiry_number ? project.enquiry_number : project.hashid
				}else{
					return '-DELETED-'
				}
			},
			niceDate(dateString){
				// let options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
				let theDate = new Date(dateString)
				return theDate.toLocaleString("en-GB")
			},
			timeSince(date) {
				date = new Date(date)

				let seconds = Math.floor((new Date() - date) / 1000);

				let interval = seconds / 31536000;

				if (interval > 1) {
					return Math.floor(interval) + " years ago";
				}
				interval = seconds / 2592000;
				if (interval > 1) {
					return Math.floor(interval) + " months ago";
				}
				interval = seconds / 86400;
				if (interval > 1) {
					return Math.floor(interval) + " days ago";
				}
				interval = seconds / 3600;
				if (interval > 1) {
					return Math.floor(interval) + " hours ago";
				}
				interval = seconds / 60;
				if (interval > 1) {
					return Math.floor(interval) + " minutes ago";
				}
				return Math.floor(seconds) + " seconds ago";
			},
			dismissAndGoto(activityID){
				this.showNotifications = false
				this.$inertia.post(`/activity/dismiss-and-go`, { activityID: activityID })
			},
			dismissAll(){
				this.showNotifications = false
				this.$inertia.post(`/activity/dismiss-all`)
			},
			niceUserTypeDisplay(userType){
				switch(userType){
					case 'admin':
						return 'Super Admin'
						break
					case 'user':
						return 'User'
						break
					// TODO: add in user's tag (surgeon or rep)
				}
			}
		},

		watch: {
			'$page.props.flash.message': function (payload) {
				// console.log('flash message', payload)
				if(payload != null && payload != ''){
					// if payload contains the word 'error' or 'unsuccessful' then show error toast
					if(payload.toLowerCase().includes('error') || payload.toLowerCase().includes('unsuccessful')){
						this.$toast.error(payload)
					}else{
						this.$toast.success(payload)
					}
				}

			},
			'$page.props.flash.error': function (payload) {
				if(payload != null && payload != ''){
					this.$toast.error(payload)
				}
			}
		}
	}
</script>

<style lang="sass">

$themeRed: #98181C

$topBarHeight: 80px
$topBarHeightMobile: 60px
$footerHeight: 55px
$sidebarWidth: 310px
$collapsedSidebarWidth: 70px

/* width */
::-webkit-scrollbar
	width: 8px

/* Track */
::-webkit-scrollbar-track
	background: rgba(#000, 0.2)

/* Handle */
::-webkit-scrollbar-thumb
	background: #111

/* Handle on hover */
::-webkit-scrollbar-thumb:hover
	background: #000


.v-toast
	padding: 0
	top: 100px
	right: 25px


.app-layout
	background-color: #fff
	min-height: 100vh
	position: relative

	a
		transition: all 300ms

		&:hover
			color: #AEEEA2

	.grey-gradient
		position: fixed
		top: 0
		left: 0
		right: 0
		height: 80vh
		background: linear-gradient(rgba(#E1E5E8, 1), rgba(#E1E5E8, 0))

	.top-bar
		height: $topBarHeight
		position: fixed
		z-index: 22
		background-color: #fff
		top: 0
		left: 0
		right: 0
		border-bottom: 5px solid $themeRed
		display: flex
		justify-content: space-between

		@media (max-width: 768px)
			height: $topBarHeightMobile
			z-index: 30

		.logo-container
			height: 100%
			margin-left: 20px
			display: inline-flex
			align-items: center

			img
				height: 28px
				width: auto

			@media (max-width: 768px)
				margin-left: 15px

				img
					height: 20px
					padding-right: 10px


		.user-account-box
			height: 100%
			position: relative
			display: flex
			align-items: center
			gap: 25px

			.user-type
				margin-left: 20px
				text-transform: uppercase
				font-size: 12px
				letter-spacing: 1px

			@media (max-width: 768px)
				position: initial

				.user-type
					display: none
					font-size: 10px
					margin-left: 10px

			.notifications-area
				// margin-right: 20px
				position: relative

				@media (max-width: 768px)
					// margin-right: 20px
					position: initial

				.fal
					font-size: 24px
					color: #111
					cursor: pointer

					@media (max-width: 768px)
						font-size: 20px

				&.new-notifications
					&:after
						content: ''
						width: 11px
						height: 11px
						background-color: #df0000
						border-radius: 50%
						position: absolute
						border: 1px solid #fff
						right: -3px
						top: -2px

				.notifications-box
					background-color: #272727
					position: absolute
					top: 40px
					right: -20px
					width: 400px
					height: auto
					box-shadow: 0 8px 25px rgba(#000, 0.5)
					z-index: 23
					visibility: hidden
					opacity: 0
					transition: all 700ms
					transform: translateY(20px)
					display: flex

					@media (max-width: 768px)
						width: auto
						left: 0
						right: 0
						top: 58px

					&:before
						content: ''
						width: 16px
						height: 16px
						background-color: #272727
						top: -8px
						right: 22px
						z-index: 9
						transform: rotate(45deg)
						position: absolute

						@media (max-width: 768px)
							display: none

					&.active
						opacity: 1
						visibility: visible
						transform: translateY(0)

					.notifications-empty
						width: 100%
						height: 100%
						display: flex
						align-items: center
						justify-content: center
						font-weight: 400
						font-family: 'Roboto Condensed', sans-serif
						color: rgba(#fff, 0.6)
						font-size: 18px
						padding: 30px

					.has-notifications
						display: flex
						flex-direction: column
						width: 100%
						padding-bottom: 40px
						position: relative
						padding-top: 10px

						.list-container
							display: flex
							flex-direction: column
							width: 100%
							height: 100%
							min-height: 400px
							max-height: 60vh
							overflow-y: auto

						.dismiss-all
							position: absolute
							bottom: 0
							left: 0
							right: 0
							height: 40px
							display: flex
							align-items: center
							background-color: rgba(#000, 0.1)
							z-index: 2
							justify-content: center
							padding: 0 20px
							color: #5AAE4B
							font-family: 'Roboto Condensed', sans-serif
							font-size: 15px
							font-weight: 700
							transition: all 300ms
							cursor: pointer

							&:hover
								background-color: rgba(#fff, 0.05)

						.row
							width: 100%
							display: flex
							flex-direction: column
							padding: 15px 20px
							transition: all 300ms
							cursor: pointer

							&:hover
								background-color: rgba(#fff, 0.05)

							.activity
								display: flex
								width: 100%
								justify-content: space-between
								
								.message
									color: rgba(#fff, 0.8)
									font-size: 16px
									font-family: 'Roboto Condensed'
									font-weight: 400
									line-height: 1.1

								.actions
									display: flex
									justify-content: space-between

									.fal
										font-size: 12px
										color: #5AAE4B

									.goto
										width: 20px
										height: 20px
										border: 1px solid #5AAE4B
										border-radius: 50%
										margin-right: 6px
										display: flex
										align-items: center
										justify-content: center
										transition: all 300ms
										cursor: pointer

										&:hover
											background-color: #fff
											border-color: #fff

											.fal
												color: #5AAE4B

									.dismiss
										width: 20px
										height: 20px
										border: 1px solid #5AAE4B
										border-radius: 50%
										display: flex
										align-items: center
										justify-content: center
										transition: all 300ms
										cursor: pointer

										&:hover
											background-color: #fff
											border-color: #fff
											
											.fal
												color: #5AAE4B

							.meta-bar
								padding-top: 5px
								.date
									color: rgba(#fff, 0.5)
									font-size: 13px
									font-weight: 400
									font-family: 'Roboto', sans-serif
								


			.dark-box
				height: 100%
				position: relative
				padding: 0 30px
				background-color: $themeRed
				min-width: 200px
				display: flex
				align-items: center

				.mobile-hamburger
					display: none

				.avatar
					width: 40px
					height: 40px
					border-radius: 50%
					overflow: hidden

				.user-details
					margin-left: 15px
					color: #fff

					.user-name
						font-family: 'Roboto Condensed', sans-serif
						font-weight: 700
						font-size: 18px

					.sign-out
						font-family: 'Roboto Condensed', sans-serif
						font-weight: 400
						font-size: 12px

						.fal
							margin-left: 5px

				@media (max-width: 768px)
					padding: 0 15px 0 20px
					min-width: 30px

					.mobile-hamburger
						width: 35px
						height: 40px
						display: flex
						position: relative
						align-items: center
						justify-content: center

						&:before, &:after
							content: ''
							width: 100%
							height: 2px
							background-color: #fff
							position: absolute
							width: 80%
							transition: all 300ms

						&:before
							transform: translateY(-4px)

						&:after
							transform: translateY(4px)

						&.active
							&:before
								transform: translateY(0) rotate(45deg)

							&:after
								transform: translateY(0) rotate(-45deg)

					.avatar
						width: 30px
						height: 30px

					.user-details
						margin-left: 10px

						.user-name
							display: none

						.sign-out
							display: none

		// &.is-admin
		// 	background-color: #181818

		// 	.logo-container
		// 		.user-type
		// 			color: #fff

		// 	.user-account-box
		// 		.notifications-area
		// 			.fal
		// 				color: #fff

		// 			&.new-notifications
		// 				&:after
		// 					border: 1px solid #181818

	.side-bar
		width: $sidebarWidth
		min-height: calc(100vh - #{$footerHeight} - #{$topBarHeight})
		position: fixed
		top: $topBarHeight
		left: 0
		background-color: rgba(#fff, 0.7)
		z-index: 8
		overflow: hidden
		transition: all 300ms

		ul.side-nav
			padding: 50px 30px 50px 50px
			list-style-type: none

			li
				padding: 0
				margin: 0

				a
					padding: 16px 0
					font-family: 'Roboto Condensed', sans-serif
					color: #111
					font-size: 18px
					font-weight: 700
					display: flex
					align-items: center
					transition: all 300ms

					.label
						white-space: nowrap
						overflow: hidden
						text-overflow: ellipsis
						max-width: 100%

					.menu-icon
						width: 40px
						cursor: pointer

					.fal
						font-size: 22px

					&:hover
						color: darken(#AEEEA2, 60%)

		&.collapsed
			@media (min-width: 768px)
				width: $collapsedSidebarWidth

				ul.side-nav
					padding: 50px 10px
					li
						a
							justify-content: center

							.label
								display: none

							.menu-icon
								width: 100%
								display: flex
								justify-content: center

							.fal
								font-size: 22px
		
		@media (max-width: 768px)
			top: 60px
			left: 0
			right: 0
			bottom: 0
			width: 100%
			background-color: #fff
			opacity: 0
			visibility: hidden
			transform: translateY(-100%)
			z-index: 30
			overflow: auto

			ul.side-nav
				padding: 30px 20px 50px 30px
				list-style-type: none

				li
					a
						padding: 12px 0
						font-size: 15px

			&.mobile-nav-open
				opacity: 1
				visibility: visible
				transform: translateY(0)

			.collapse-sidebar-toggle
				display: none

	main
		position: relative
		padding-top: $topBarHeight
		padding-left: $sidebarWidth
		min-height: calc(100vh - #{$footerHeight})
		height: auto
		transition: padding-left 300ms
		// overflow-x: hidden

		&.collapsed-sidebar
			padding-left: $collapsedSidebarWidth

		.content-container
			padding: 50px
			display: flex
			justify-content: center
			overflow-x: hidden

			.inner-page
				width: 100%
				max-width: 90rem

		@media (max-width: 768px)
			padding-left: 0

			&.collapsed-sidebar
				padding-left: 0

			.content-container
				padding: 0 15px

				.inner-page
					max-width: 100%

	footer
		height: $footerHeight
		width: 100%
		position: relative
		background-color: #f6f6f6
		padding: 0 25px
		display: flex
		align-items: center
		font-size: 12px
		font-weight: 700
		color: rgba(#000, 0.4)

.fade-enter-active, .fade-leave-active
	transition: opacity .3s

.fade-enter, .fade-leave-to
	opacity: 0

.slide-fade-enter-active, .slide-fade-leave-active
	transition: all 1200ms

.slide-fade-enter, .slide-fade-leave-to
	opacity: 0
	transform: translateX(60px)


.flash-alert
	position: fixed
	z-index: 200
	top: 120px
	right: 30px
	border-radius: 8px
	box-shadow: 0 2px 8px rgba(#000,0.2)
	background-color: #E0F5DD
	color: #3E8732
	padding: 30px 40px
	font-size: 18px
	font-weight: 700
	font-family: 'Roboto Condensed', sans-serif


</style>
